import { useEffect, useRef, useState } from "react"
import { getCheckerCompanions } from "../services/checkerService"
import {
    acceptedCheckerCompanions,
    activeCheckerCompanions,
    checkerProgressPills,
    deriveCheckerStatus,
    formatCheckinDateTime,
    formatCountdown,
    isImSafeEnabled,
} from "../utilities/checker"
import { useTickingClock } from "../hooks/useTickingClock"
import CheckerStatusPill from "./CheckerStatusPill"
import CheckerProgressPills from "./CheckerProgressPills"
import CheckerCompanionAvatars from "./CheckerCompanionAvatars"

const SAFE_COLOR = "#198754"
const DANGER_COLOR = "#dc3545"
const SCHEDULED_COLOR = "#6c757d"

/**
 * An owner's active or scheduled Checker trip, modeled on the Android app's
 * ActiveTripCard: status pill, title (click to edit), location, last
 * check-in, a progress-pill row, next-check-in/cadence beside the convoy's
 * avatar stack, and (once active) the safe/danger buttons plus lightweight
 * extend/cancel text actions.
 *
 * @param {Object} trip - Raw trip fields from GET /v1/checker/get
 * @param {number} refreshToken - Bump to force a companion refetch (e.g.
 *   after loadTrips(), since a companion's own response isn't otherwise
 *   reflected until this card's own next mount)
 * @param {boolean} isActionPending - Disables this card's own buttons while
 *   one of its actions is in flight
 * @param {(tripId: number) => void} onEdit
 * @param {(tripId: number) => void} onAutoActivate - Fired once, the instant
 *   a scheduled trip's own countdown passes its due time
 * @param {(status: "safe"|"danger") => void} onCheckin
 * @param {() => void} onExtend
 * @param {() => void} onCancel
 */
export default function CheckerTripCard({
    trip,
    refreshToken,
    isActionPending,
    onEdit,
    onAutoActivate,
    onCheckin,
    onExtend,
    onCancel,
    onShowEvents,
}) {
    const now = useTickingClock()
    const { displayStatus, isOverdue, dueMillis } = deriveCheckerStatus(trip, now)
    const isActive = displayStatus === "active"
    const isScheduled = displayStatus === "scheduled"

    const nextCheckinText = dueMillis == null ? "--:--" : formatCountdown(dueMillis - now)
    const imSafeEnabled = isImSafeEnabled(trip, dueMillis, now)

    // Fires the moment a scheduled trip's own countdown tips over, same as
    // the Android app -- guarded so it only ever fires once per card, not
    // once per tick, and doesn't re-fire once the resulting refetch flips
    // trip.status to "active" and re-renders this same card.
    const hasRequestedActivation = useRef(false)
    useEffect(() => {
        if (isScheduled && isOverdue && !hasRequestedActivation.current) {
            hasRequestedActivation.current = true
            onAutoActivate(trip.id)
        }
    }, [isScheduled, isOverdue, onAutoActivate, trip.id])

    const [companions, setCompanions] = useState([])
    useEffect(() => {
        let cancelled = false
        getCheckerCompanions(trip.id)
            .then((rows) => {
                if (!cancelled) setCompanions(Array.isArray(rows) ? rows : [])
            })
            .catch(() => {
                // Leave the last-known list in place; the summary just
                // won't reflect a companion change until the next refetch.
            })
        return () => {
            cancelled = true
        }
    }, [trip.id, refreshToken])

    const active = activeCheckerCompanions(companions)
    const accepted = acceptedCheckerCompanions(companions)
    const needsAttention = trip.safety_status === "danger" || isOverdue
    const pills = checkerProgressPills(
        trip.expected_checkins_count,
        trip.completed_checkins_count,
        needsAttention,
    )

    return (
        <article
            className={`checker-trip-card${isActive && isOverdue ? " checker-trip-card-danger" : ""}`}
        >
            <div className="checker-trip-status-row">
                {/* No Android equivalent -- a small, unobtrusive addition
                    rather than a card-face button, so it doesn't compete
                    with the layout this card is otherwise matching. */}
                <button
                    type="button"
                    className="checker-trip-timeline-button"
                    onClick={onShowEvents}
                    title="View timeline"
                    aria-label="View timeline"
                >
                    <i className="bi bi-clock-history" aria-hidden="true"></i>
                </button>
                {isActive ? (
                    <CheckerStatusPill
                        label="Active"
                        color={SAFE_COLOR}
                        pulsing={!isOverdue}
                    />
                ) : (
                    <CheckerStatusPill label="Scheduled" color={SCHEDULED_COLOR} filled />
                )}
            </div>

            <div className="checker-trip-heading">
                <button
                    type="button"
                    className="checker-trip-title-button"
                    onClick={() => onEdit(trip.id)}
                >
                    <span className="checker-trip-title">{trip.title}</span>
                    <i className="bi bi-pencil checker-trip-title-icon" aria-hidden="true"></i>
                </button>
                <div className="checker-trip-meta">
                    📍 {trip.place_label || "Destination not set"}
                </div>
                <div className="checker-trip-meta">
                    Last check-in: {formatCheckinDateTime(trip.last_checkin_at)}
                </div>
            </div>

            <CheckerProgressPills pills={pills} />

            <div className="checker-trip-stats-row">
                <div className="checker-trip-stats-left">
                    <div className="checker-trip-stat">
                        <div className="checker-trip-stat-label">Next check-in</div>
                        <div
                            className={`checker-trip-stat-value${isOverdue ? " checker-trip-stat-danger" : ""}`}
                        >
                            {nextCheckinText}
                        </div>
                    </div>
                    <div className="checker-trip-stat">
                        <div className="checker-trip-stat-label">Cadence</div>
                        <div className="checker-trip-stat-value">
                            {trip.cadence_minutes} mins
                        </div>
                    </div>
                </div>

                <CheckerCompanionAvatars
                    acceptedCompanions={accepted}
                    totalCount={active.length}
                />
            </div>

            {isActive && (
                <>
                    <div className="checker-trip-checkin-row">
                        <button
                            type="button"
                            className="btn checker-btn-safe"
                            disabled={!imSafeEnabled || isActionPending}
                            onClick={() => onCheckin("safe")}
                        >
                            I&apos;m safe
                        </button>
                        <button
                            type="button"
                            className="btn checker-btn-danger"
                            disabled={isActionPending}
                            onClick={() => onCheckin("danger")}
                        >
                            Need help
                        </button>
                    </div>

                    <div className="checker-trip-text-actions">
                        <button
                            type="button"
                            className="checker-trip-text-action"
                            disabled={isActionPending}
                            onClick={onExtend}
                        >
                            Extend
                        </button>
                        <button
                            type="button"
                            className="checker-trip-text-action"
                            disabled={isActionPending}
                            onClick={onCancel}
                        >
                            Cancel
                        </button>
                    </div>
                </>
            )}
        </article>
    )
}

export { SAFE_COLOR, DANGER_COLOR, SCHEDULED_COLOR }
